import isNil from 'lodash/isNil';

import {getBackendCurrencyValue, getDateFromIsoString} from '@shared/api';
import {
  AutoCancel,
  Order,
  OrderFullInfo,
  OrderItem,
  OrderItemOwnership,
  StopLoss,
} from '../types';
import {
  AutoCancelOnBackend,
  OrderFullInfoOnBackend,
  OrderItemOnBackend,
  OrderItemOwnershipOnBackend,
  OrderOnBackend,
  StopLossOnBackend,
} from './backendTypes';

export const mapAutoCancelFromBackend = ({
  order_id,
  timeout_at,
  status,
}: AutoCancelOnBackend): AutoCancel => ({
  orderId: order_id ?? '',
  timeoutAt: getDateFromIsoString(timeout_at),
  status,
});

export const mapStopLossFromBackend = ({
  order_id,
  type,
  price,
  status,
}: StopLossOnBackend): StopLoss => ({
  orderId: order_id,
  triggerType: type,
  price: getBackendCurrencyValue(price) as number,
  status,
});

export const mapOrderFromBackend = ({
  id,
  created_at,
  user_id,
  action,
  type,
  side,
  event_id,
  requested_items_quantity,
  requested_max_inv,
  matched_quantity,
  exited_quantity,
  price,
  slippage,
  matching,
  labels,
  auto_cancel,
  stop_loss,
  take_profit,
}: OrderOnBackend): Order => ({
  id,
  created: getDateFromIsoString(created_at),
  userId: user_id,
  action,
  orderType: type,
  choice: side,
  eventId: event_id,
  requestedItemCount: requested_items_quantity,
  requestedMaxInv: getBackendCurrencyValue(requested_max_inv) as number,
  matchedQuantity: matched_quantity,
  exitedQuantity: exited_quantity,
  price: getBackendCurrencyValue(price),
  slippage: getBackendCurrencyValue(slippage),
  matching: matching ?? null,
  labels: labels ?? [],
  auto_cancel: isNil(auto_cancel)
    ? null
    : mapAutoCancelFromBackend(auto_cancel),
  stop_loss: isNil(stop_loss) ? null : mapStopLossFromBackend(stop_loss),
  take_profit: isNil(take_profit) ? null : mapStopLossFromBackend(take_profit),
});

export const mapOrderFullInfoFromBackend = ({
  id,
  event_id,
  action,
  start_date,
  end_date,
  type,
  side,
  requested_items_quantity,
  executed_items_quantity,
  requested_max_inv,
  executed_max_inv,
  investment,
  invested,
  current_value,
  live_profit,
  auto_cancel,
  stop_loss,
  take_profit,
  price,
  slippage,
  selling_price,
  potential_profit,
  avg_exit_price,
  unfilled,
  matched_quantity,
  matching,
  labels,
  profit,
  cancelled,
}: OrderFullInfoOnBackend): OrderFullInfo => ({
  id,
  eventId: event_id,
  action,
  startDate: getDateFromIsoString(start_date),
  endDate: getDateFromIsoString(end_date),
  orderType: type,
  choice: side,
  requested_items_quantity: requested_items_quantity ?? null,
  executed_items_quantity: executed_items_quantity ?? null,
  requested_max_inv: getBackendCurrencyValue(requested_max_inv),
  executed_max_inv: getBackendCurrencyValue(executed_max_inv),
  investment: getBackendCurrencyValue(investment),
  invested: getBackendCurrencyValue(invested),
  current_value: getBackendCurrencyValue(current_value),
  live_profit: getBackendCurrencyValue(live_profit),
  auto_cancel: isNil(auto_cancel)
    ? null
    : mapAutoCancelFromBackend(auto_cancel),
  stop_loss: isNil(stop_loss) ? null : mapStopLossFromBackend(stop_loss),
  take_profit: isNil(take_profit) ? null : mapStopLossFromBackend(take_profit),
  price: getBackendCurrencyValue(price),
  slippage: getBackendCurrencyValue(slippage),
  selling_price: getBackendCurrencyValue(selling_price),
  potential_profit: getBackendCurrencyValue(potential_profit),
  avg_exit_price: getBackendCurrencyValue(avg_exit_price),
  unfilled: getBackendCurrencyValue(unfilled),
  matched_quantity: matched_quantity ?? null,
  matching: matching ?? null,
  labels: labels ?? [],
  profit: getBackendCurrencyValue(profit),
  cancelled: cancelled ?? null,
});

const mapOrderItemOwnershipFromBackend = ({
  owner_user_id,
  owner_order_id,
  acquired_at,
  released_at,
  release_reason,
  release_order_id,
  seller_user_id,
  seller_order_id,
  is_current_order,
}: OrderItemOwnershipOnBackend): OrderItemOwnership => ({
  ownerUserId: owner_user_id,
  ownerOrderId: owner_order_id,
  acquired: getDateFromIsoString(acquired_at),
  released: released_at ? getDateFromIsoString(released_at) : null,
  releaseReason: release_reason ?? '',
  releaseOrderId: release_order_id ?? '',
  sellerUserId: seller_user_id ?? '',
  sellerOrderId: seller_order_id ?? '',
  isCurrentOrder: is_current_order ?? false,
});

export const mapOrderItemFromBackend = ({
  id,
  created_at,
  updated_at,
  side,
  role,
  price,
  real,
  promo,
  user_id,
  counterparty_side,
  counterparty_user_id,
  counterparty_order_id,
  ownership,
  pair_item_id,
}: OrderItemOnBackend): OrderItem => ({
  id,
  created: getDateFromIsoString(created_at),
  updated: getDateFromIsoString(updated_at),
  side,
  role,
  price: getBackendCurrencyValue(price) as number,
  real: getBackendCurrencyValue(real) as number,
  promo: getBackendCurrencyValue(promo) as number,
  userId: user_id,
  counterpartySide: counterparty_side,
  counterpartyUserId: counterparty_user_id ?? '',
  counterpartyOrderId: counterparty_order_id ?? '',
  ownership: ownership?.map(mapOrderItemOwnershipFromBackend) ?? [],
  pairItemId: pair_item_id ?? '',
});
